import { AfterViewInit, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthController } from '@domain';
import { filter, map } from 'rxjs';
import { RaToastComponent } from '../../../../shared/components/ra-toast/ra-toast.component';
import { RaUploadProgressBarComponent } from '../../../../shared/components/ra-upload-progress-bar/ra-upload-progress-bar.component';
import { AppSessionService } from '../../../../shared/services/app-session.service';
import { MobileDetectionService } from '../../../../shared/services/mobile-detection.service';
import { MobileGateComponent } from '../../components/mobile-gate/mobile-gate.component';
import { SplashScreenComponent } from '../../components/splash-screen/splash-screen.component';
import { CoreService } from '../../services/core.service';

/**
 * Rotas embutidas: renderizadas pela web e consumidas por FORA dela.
 *
 * O preview é renderizado pela web e consumido pelo app nativo dentro de um WebView
 * (`document/:id/pdf-preview?hideMenu=true&previewToken=...`), além de ser o destino dos links
 * de compartilhamento abertos por terceiros no navegador do celular. Nesses dois casos nem a
 * tela de download do app nem a tela de abertura devem aparecer.
 *
 * Três rotas entram (o prefixo antes de `preview` é opcional no padrão): `pdf-preview` (o
 * preview paginado com paged.js, que o app abre), `preview` (o link de compartilhamento) e
 * `cover-preview` (a folha da capa, que o app abre na tela de capa).
 *
 * Exportada e pura de propósito: quando o app trocou a rota do webview de `preview` para
 * `pdf-preview`, o padrão antigo deixou de bater e o app inteiro cairia na tela de "baixe o
 * aplicativo", sem erro nenhum no caminho. É o tipo de regra que precisa de teste.
 */
export function isEmbeddedRoute(url: string): boolean {
    return /^\/document\/[^/]+\/(?:pdf-|cover-)?preview(?:[?#]|$)/.test(url);
}

/**
 * Rotas que a trava do mobile cobre: só a plataforma logada (`/platform/**`).
 *
 * A trava começou cobrindo o site inteiro, com o preview como única exceção, e isso
 * barrava o onboarding no celular: quem abria o welcome kit pelo navegador chegava ao
 * passo de planos (ou voltava do checkout do Stripe) e dava de cara com "baixe o
 * aplicativo". O cadastro, o pagamento pelo Stripe e as rotas públicas (login, convite,
 * troca de senha, política, exclusão de conta) precisam funcionar 100% no navegador do
 * celular: nem todo mundo quer pagar pela Apple ou pelo Google. O app só é oferecido
 * depois, quando o usuário já está dentro da plataforma.
 *
 * O Stripe devolve para `${origin}/#/platform` (ver `SubscriptionService.createSession`
 * na API), então a volta do pagamento é a primeira tela em que a trava aparece.
 */
export function isMobileGatedRoute(url: string): boolean {
    return /^\/platform(?:[/?#]|$)/.test(url);
}

/**
 * Se a tela de abertura deve nascer montada.
 *
 * Ela existe para cobrir a primeira carga da plataforma: a empresa corrente, os
 * feature flags, os convites, a contagem de notificações. Nada disso é buscado
 * sem sessão (`CoreService.initAllGlobalRequests` volta na hora), então para
 * quem chega deslogado ela não cobre carga nenhuma: cobre a viagem até o login,
 * com um "Abrindo os seus documentos" na frente de quem ainda não tem
 * documento nenhum aberto. Pior, quem nunca esteve aqui não tem marca de
 * presença, e a volta conta como chegada: o primeiro acesso ao site ganhava os
 * cinco segundos inteiros de animação antes de ver o formulário de login.
 *
 * Ela também não existe nas rotas embutidas: são webviews do app e links de
 * compartilhamento, e ali uma abertura do RapiDocs seria uma capa estranha em
 * cima do documento de outra pessoa.
 *
 * O gêmeo no app é `shouldHoldSplash`, em
 * `magic-reports-app/lib/src/_modules/_splashscreen/splashscreen.store.dart`.
 * Lá a tela é a raiz do roteamento e não pode deixar de existir, então o que
 * cai para quem está deslogado é a espera, não a tela.
 */
export function shouldShowSplash(options: {
    isEmbeddedRoute: boolean;
    isLoggedIn: boolean;
}): boolean {
    return !options.isEmbeddedRoute && options.isLoggedIn;
}

@Component({
    selector: 'core',
    templateUrl: './core.page.html',
    standalone: true,
    imports: [
        RouterOutlet,
        RaUploadProgressBarComponent,
        RaToastComponent,
        MobileGateComponent,
        SplashScreenComponent,
    ],
})
export class CorePage implements OnInit, AfterViewInit {
    private _router = inject(Router);
    private _coreService = inject(CoreService);
    private _mobileDetection = inject(MobileDetectionService);
    private _appSession = inject(AppSessionService);
    private _authController = inject(AuthController);

    private readonly _currentUrl = toSignal(
        this._router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
        ),
        { initialValue: this._readInitialUrl() },
    );

    /**
     * No mobile a plataforma é "travada": não montamos nem inicializamos nada dela.
     * Só a plataforma logada (ver `isMobileGatedRoute`): onboarding, pagamento e as
     * rotas públicas seguem funcionando no navegador do celular.
     */
    protected readonly showMobileGate = computed(
        () => this._mobileDetection.isMobile() && isMobileGatedRoute(this._currentUrl()),
    );

    /**
     * A tela de abertura. Ela nasce montada e sai sozinha (ver
     * `SplashScreenComponent`): quem decide o tempo dela é ela mesma, a partir de
     * quanto tempo o usuário passou fora.
     *
     * Quando ela nasce (sessão em mãos, fora das rotas embutidas) é regra à
     * parte, e testada: ver `shouldShowSplash`.
     */
    protected readonly showSplash = signal(
        shouldShowSplash({
            isEmbeddedRoute: this._isEmbeddedRoute(),
            isLoggedIn: this._authController.isLoggedIn(),
        }),
    );

    protected readonly bootstrapDone = signal(false);

    ngOnInit(): void {
        if (this.showMobileGate()) {
            this.showSplash.set(false);
            return;
        }

        this._appSession.trackPresence();

        // A carga começa aqui, no mesmo instante em que a tela de abertura
        // aparece: o tempo mínimo dela é um piso para a animação, e não uma
        // espera somada à rede.
        void this._coreService.initAllGlobalRequests().finally(() => this.bootstrapDone.set(true));
    }

    ngAfterViewInit(): void {
        this._dismissCompanySwitchOverlay();
    }

    private _isEmbeddedRoute(): boolean {
        return isEmbeddedRoute(this._readInitialUrl());
    }

    /**
     * No primeiro render o router ainda não emitiu `NavigationEnd` (e `router.url`
     * ainda é `/`), então lemos a rota direto do browser. Como o app usa
     * `HashLocationStrategy`, ela vive no hash (`/#/document/:id/preview?...`).
     */
    private _readInitialUrl(): string {
        if (typeof window === 'undefined') return this._router.url;

        const hash = window.location.hash;
        if (hash.startsWith('#')) return hash.slice(1);

        return window.location.pathname + window.location.search;
    }

    /**
     * Remove o overlay de transição de troca de empresa (declarado no index.html)
     * após o shell da plataforma pintar, com um fade suave. No-op quando não há
     * troca em andamento.
     */
    private _dismissCompanySwitchOverlay(): void {
        const overlay = document.getElementById('company-switch-overlay');
        if (!overlay || !overlay.classList.contains('visible')) return;

        // Pequeno atraso para o shell pintar antes do fade-out.
        setTimeout(() => {
            overlay.classList.add('hide');
            setTimeout(() => overlay.remove(), 400);
        }, 600);
    }
}
